import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiGet } from "../Api/Client.js";
import "../Styles/TaskDescription.css";

/* A task's description (agents and people write findings, files, fixes and handoffs there).
   A small bordered "..." pill before the title says there is one; a click opens it under the row as Markdown.
   The text is plain Markdown only (no raw HTML is rendered).

   Home's task list already carries the text (`task.description`); the project board's rows only say
   `has_description`, and the text is read on the first click (GET /Project/tasks/<id>/, active members only).

   Use: const desc = useTaskDescription(task);  ... {desc.pill} next to the title ... {desc.panel} under the row. */
export function useTaskDescription(task) {
  const inline = typeof task.description === "string" ? task.description : null;
  const has = task.has_description === true || Boolean((inline || "").trim());
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(inline);
  const [state, setState] = useState(inline !== null ? "ready" : "idle");

  if (!has) return { pill: null, panel: null };

  const toggle = (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && state === "idle") {
      setState("loading");
      apiGet(`/Project/tasks/${task.id}/`)
        .then((data) => { setText(data.description || ""); setState("ready"); })
        .catch(() => setState("error"));
    }
  };

  const pill = (
    <button
      type="button"
      className={"TaskDesc-Pill" + (open ? " open" : "")}
      onClick={toggle}
      aria-expanded={open}
      aria-label={open ? "Hide the description" : "Show the description"}
      title="This task has a description"
    >
      <span aria-hidden="true">···</span>
    </button>
  );

  const panel = open ? (
    <div className="TaskDesc-Panel" onClick={(e) => e.stopPropagation()}>
      {state === "loading" && <span className="TaskDesc-Note">Loading…</span>}
      {state === "error" && <span className="TaskDesc-Note">Could not load the description. Close and try again.</span>}
      {state === "ready" && <ReactMarkdown remarkPlugins={[remarkGfm]}>{text || ""}</ReactMarkdown>}
    </div>
  ) : null;

  return { pill, panel };
}

/* For rows built inside a loop, where a hook cannot be called: <WithTaskDescription task={t}>{(desc) => ...}</WithTaskDescription> */
export function WithTaskDescription({ task, children }) {
  return children(useTaskDescription(task));
}
